import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, LogLevel, HttpTransportType } from '@microsoft/signalr';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../features/auth/services/auth.service';

export interface PaymentNotification {
  orderId: string;
  orderCode: string;
  status: 'Success' | 'Failed';
  transactionId?: string;
  amount?: number;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class SignalrService {
  private hubConnection: HubConnection | null = null;
  private paymentNotificationSubject = new BehaviorSubject<PaymentNotification | null>(null);
  public paymentNotification$ = this.paymentNotificationSubject.asObservable();
  private connectionPromise: Promise<void> | null = null;
  private connectionUrl = environment.signalRHubUrl || `${environment.apiURL}/api/paymentHub`;

  constructor(private authService: AuthService) {}

  private async startConnectionInternal(): Promise<void> {
    const token = this.authService.getToken();
    
    if (!token) {
      return Promise.reject('No auth token');
    }

    if (this.authService.isTokenExpired(token)) {
      return Promise.reject('Token expired');
    }
    
    this.hubConnection = new HubConnectionBuilder()
      .withUrl(this.connectionUrl, {
        accessTokenFactory: () => token,
        transport: HttpTransportType.WebSockets
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();

    this.hubConnection.on('PaymentCompleted', (notification: PaymentNotification) => {
      // Normalize payload just in case BE sends PascalCase properties
      const normalizedData: PaymentNotification = {
        orderId: (notification as any).orderId || (notification as any).OrderId,
        orderCode: (notification as any).orderCode || (notification as any).OrderCode,
        status: (notification as any).status || (notification as any).Status,
        transactionId: (notification as any).transactionId || (notification as any).TransactionId,
        amount: (notification as any).amount || (notification as any).Amount,
        message: (notification as any).message || (notification as any).Message,
      };
      this.paymentNotificationSubject.next(normalizedData);
    });
    
    this.hubConnection.on('JoinedPaymentGroup', (orderId: string) => {
      // Group join confirmed
    });

    this.hubConnection.onclose(error => {
      this.hubConnection = null;
      this.connectionPromise = null;
    });

    try {
      await this.hubConnection.start();
    } catch (err) {
      this.hubConnection = null;
      this.connectionPromise = null;
      throw err;
    }
  }

  public ensureConnected(): Promise<void> {
    if (!this.connectionPromise) {
      this.connectionPromise = this.startConnectionInternal();
    }
    return this.connectionPromise;
  }

  public async joinGroup(orderId: string): Promise<void> {
    try {
      await this.ensureConnected();
      if (this.hubConnection?.state === 'Connected') {
        await this.hubConnection.invoke('JoinPaymentGroup', orderId);
      } else {
        throw new Error('SignalR connection is not in the "Connected" state.');
      }
    } catch (err) {
      throw err;
    }
  }

  public async leaveGroup(orderId: string): Promise<void> {
    if (this.hubConnection?.state === 'Connected') {
      await this.hubConnection.invoke('LeavePaymentGroup', orderId);
    }
  }

  public async stopConnection(): Promise<void> {
    if (this.hubConnection) {
      await this.hubConnection.stop();
      this.hubConnection = null;
      this.connectionPromise = null;
    }
  }
}
